function ringArea(ring) {
  let area = 0;
  for (let i = 0, len = ring.length, j = len - 1; i < len; j = i++) {
    area += (ring[j][0] - ring[i][0]) * (ring[j][1] + ring[i][1]);
  }
  return Math.abs(area / 2);
}

function ringCentroid(ring) {
  let x = 0, y = 0;
  for (const [lng, lat] of ring) { x += lng; y += lat; }
  const n = ring.length;
  return n ? [x / n, y / n] : null;
}

export function precomputeCentroids(geoFeatures) {
  if (!geoFeatures?.length) return new Map();
  const grouped = new Map();
  for (const f of geoFeatures) {
    const id = String(f.properties?.area_id ?? "");
    if (!id) continue;
    if (!grouped.has(id)) grouped.set(id, []);
    grouped.get(id).push(f);
  }
  const result = new Map();
  for (const [id, feats] of grouped) {
    let bestCentroid = null, bestArea = -1;
    for (const f of feats) {
      const geom = f?.geometry;
      if (!geom) continue;
      const rings = geom.type === "Polygon"
        ? [geom.coordinates[0]]
        : geom.type === "MultiPolygon"
          ? geom.coordinates.map((p) => p[0])
          : [];
      for (const ring of rings) {
        if (!ring?.length) continue;
        const a = ringArea(ring);
        if (a > bestArea) { bestArea = a; bestCentroid = ringCentroid(ring); }
      }
    }
    if (bestCentroid) result.set(id, bestCentroid);
  }
  return result;
}

function getDensityScale(n) {
  if (n > 1200) return 0.35;
  if (n > 800)  return 0.45;
  if (n > 500)  return 0.60;
  if (n > 300)  return 0.75;
  return 1;
}

export function buildCompareArrows({
  centroidMap = new Map(),
  deltaRows = [],
  valueKey = null,
  valueFn = null,
  posColor = "#15803d",
  negColor = "#b91c1c",
  posAngle = 45,
  negAngle = 225,
} = {}) {
  if (!centroidMap.size || !deltaRows.length) return { type: "FeatureCollection", features: [] };
  const getValue = valueFn ? valueFn : valueKey ? (row) => Number(row?.[valueKey]) : () => NaN;
  const allValues = [];
  const featureData = [];
  for (const row of deltaRows) {
    const id = String(row.area_id ?? "");
    const centroid = centroidMap.get(id);
    if (!centroid) continue;
    const v = getValue(row);
    if (!Number.isFinite(v) || v === 0) continue;
    allValues.push(v);
    featureData.push({ centroid, v });
  }
  if (!allValues.length) return { type: "FeatureCollection", features: [] };
  const maxAbs = Math.max(...allValues.map((v) => Math.abs(v)));
  const ds = getDensityScale(centroidMap.size);
  const sizeScale    = (v) => (0.3 + Math.sqrt(Math.min(1, Math.abs(v) / maxAbs)) * 1.5) * ds;
  const opacityScale = (v) => 0.85 + Math.min(1, Math.abs(v) / maxAbs) * 0.15;
  return {
    type: "FeatureCollection",
    features: featureData.map(({ centroid, v }) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: centroid },
      properties: {
        angle:   v >= 0 ? posAngle : negAngle,
        size:    sizeScale(v),
        opacity: opacityScale(v),
        color:   v >= 0 ? posColor : negColor,
        delta:   v,
      },
    })),
  };
}

export function registerArrowImage(map, imageName = "compare-arrow") {
  if (!map || map.hasImage?.(imageName)) return;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="32" viewBox="0 0 20 32">
    <path fill="white" d="M10 1 L18 13 L12 11 L12 31 L8 31 L8 11 L2 13 Z"/>
  </svg>`;
  const img = new Image(20, 32);
  img.onload = () => { if (!map.hasImage(imageName)) map.addImage(imageName, img, { sdf: true }); };
  img.src = "data:image/svg+xml;utf8," + encodeURIComponent(svg);
}
